// Five core services — bilingual, editable content. Each service page renders
// from this structure: hero, overview, audience, challenges, evaluation,
// areas of support, FAQs and a service-specific disclaimer.
// `status: "inactive"` keeps a service stored in the CMS but fully hidden
// from the public site (nav, cards, sitemap, direct routes).

export const SERVICES = [
  {
    slug: "business-consulting",
    num: "01",
    status: "active",
    img: "office",
    en: {
      title: "Business Consulting",
      short: "Strategic guidance for entrepreneurs and businesses seeking greater clarity in organization, administration, structure and growth.",
      heroSub: "A business that is organized can grow. One that isn't, can't.",
      seoTitle: "Business Consulting in Lake Nona, Orlando | Aviah Financial",
      seoDesc: "Business consulting for entrepreneurs and small businesses in Lake Nona, Orlando — organization, structure, administration and growth. Bilingual.",
      overview: [
        "Most business problems are organization problems wearing a disguise. Structure, administration and financial organization decide whether growth is sustainable — long before marketing does.",
        "Aviah Financial provides strategic guidance for entrepreneurs, small businesses and companies in areas related to organization, administration, structure and growth — connected to the tax, asset and estate dimensions of the owner's life.",
      ],
      whoFor: [
        "Entrepreneurs forming their first business",
        "Small business owners who have outgrown improvisation",
        "Family businesses professionalizing their operations",
        "Companies that need administrative strategy, not just more sales",
      ],
      challenges: [
        "A structure chosen quickly that no longer fits",
        "Business and personal finances entangled",
        "No administrative routine — everything lives in someone's head",
        "Growth decisions made without seeing their tax and legal consequences",
      ],
      evaluates: [
        "Your business structure and whether it still fits",
        "Organization, administration and financial routines",
        "How business decisions connect to your personal taxes and assets",
        "What needs to be built before growth is safe",
      ],
      support: [
        "Business organization and formation guidance",
        "Administrative strategy and financial organization",
        "Business structure guidance",
        "Operational clarity and routine building",
        "Coordination with tax, asset protection and estate planning",
      ],
      faqs: [
        { q: "Can you help me start a business in Florida?", a: "Yes. We guide entrepreneurs through structure selection, formation steps, financial organization and the questions worth asking before you file anything." },
        { q: "My business is running — why would I need consulting?", a: "Because running and organized are different things. Most growing businesses reach a point where improvisation becomes the biggest risk. That's usually the right moment to talk." },
        { q: "How does business consulting connect to your other services?", a: "Directly. Business decisions affect your taxes, your assets and your estate plan — which is why we evaluate the complete picture before recommending any single step." },
      ],
      disclaimer: "Business consulting provides strategic and organizational guidance. It does not replace legal, tax or licensing advice specific to your situation.",
    },
    es: {
      title: "Consultoría Empresarial",
      short: "Orientación estratégica para emprendedores y empresas que buscan mayor claridad en organización, administración, estructura y crecimiento.",
      heroSub: "Un negocio organizado puede crecer. Uno que no lo está, no.",
      seoTitle: "Consultoría Empresarial en Lake Nona, Orlando | Aviah Financial",
      seoDesc: "Consultoría empresarial para emprendedores y pequeños negocios en Lake Nona, Orlando — organización, estructura, administración y crecimiento. Bilingüe.",
      overview: [
        "La mayoría de los problemas empresariales son problemas de organización disfrazados. La estructura, la administración y la organización financiera deciden si el crecimiento es sostenible — mucho antes que el marketing.",
        "Aviah Financial ofrece orientación estratégica para emprendedores, pequeños negocios y empresas en áreas relacionadas con organización, administración, estructura y crecimiento — conectadas con las dimensiones fiscales, de activos y patrimoniales de la vida del dueño.",
      ],
      whoFor: [
        "Emprendedores formando su primer negocio",
        "Dueños de pequeños negocios que ya superaron la improvisación",
        "Negocios familiares profesionalizando sus operaciones",
        "Empresas que necesitan estrategia administrativa, no solo más ventas",
      ],
      challenges: [
        "Una estructura elegida rápidamente que ya no encaja",
        "Finanzas empresariales y personales entrelazadas",
        "Sin rutina administrativa — todo vive en la cabeza de alguien",
        "Decisiones de crecimiento tomadas sin ver sus consecuencias fiscales y legales",
      ],
      evaluates: [
        "Tu estructura empresarial y si todavía encaja",
        "Organización, administración y rutinas financieras",
        "Cómo las decisiones del negocio se conectan con tus impuestos y activos personales",
        "Qué debe construirse antes de que el crecimiento sea seguro",
      ],
      support: [
        "Orientación en organización y formación de negocios",
        "Estrategia administrativa y organización financiera",
        "Orientación en estructura empresarial",
        "Claridad operativa y construcción de rutinas",
        "Coordinación con impuestos, protección de activos y planificación patrimonial",
      ],
      faqs: [
        { q: "¿Pueden ayudarme a iniciar un negocio en Florida?", a: "Sí. Guíamos a emprendedores en la selección de estructura, los pasos de formación, la organización financiera y las preguntas que vale la pena hacer antes de presentar cualquier documento." },
        { q: "Mi negocio ya funciona — ¿por qué necesitaría consultoría?", a: "Porque funcionar y estar organizado son cosas distintas. La mayoría de los negocios en crecimiento llegan a un punto donde la improvisación se convierte en el mayor riesgo. Ese suele ser el momento correcto para hablar." },
        { q: "¿Cómo se conecta la consultoría empresarial con sus otros servicios?", a: "Directamente. Las decisiones del negocio afectan tus impuestos, tus activos y tu plan patrimonial — por eso evaluamos el panorama completo antes de recomendar cualquier paso." },
      ],
      disclaimer: "La consultoría empresarial proporciona orientación estratégica y organizacional. No reemplaza asesoría legal, fiscal o de licencias específica a tu situación.",
    },
  },
  {
    slug: "tax-accounting",
    num: "02",
    status: "active",
    img: "office",
    en: {
      title: "Financial Consulting",
      short: "Tax guidance, accounting support and complete financial organization — for individuals and businesses.",
      heroSub: "Organized records. Informed filings. No surprises.",
      seoTitle: "Financial & Tax Consulting in Lake Nona, Orlando | Aviah Financial",
      seoDesc: "Financial consulting, tax guidance and accounting support for individuals and businesses in Lake Nona, Orlando — including ITIN support. Bilingual, by appointment.",
      overview: [
        "Taxes touch nearly every other financial decision you make — your business structure, your investments, your estate plan. We approach your financial life as one connected system, with tax and accounting as its organizational backbone — not as a once-a-year transaction.",
        "Aviah Financial supports individuals and businesses with tax-related guidance, accounting support, financial organization and planning conversations — so you always know where you stand and what comes next.",
      ],
      whoFor: [
        "Individuals with personal tax obligations",
        "Self-employed professionals and contractors",
        "Small business owners who need organized books",
        "New U.S. residents navigating the tax system for the first time",
      ],
      challenges: [
        "Disorganized records that make every tax season stressful",
        "Uncertainty about which obligations apply to your situation",
        "Business and personal finances mixed together",
        "Documents in two languages and requirements in one",
      ],
      evaluates: [
        "Your current filing status and obligations",
        "How your business structure affects your taxes",
        "The state of your books and record-keeping",
        "Whether an ITIN applies to anyone in your situation",
        "How tax decisions connect to your broader strategy",
      ],
      support: [
        "Individual tax preparation support",
        "Business tax preparation support",
        "Accounting and bookkeeping organization",
        "ITIN application assistance as a Certified Acceptance Agent",
        "Financial organization systems for ongoing clarity",
      ],
      faqs: [
        { q: "Do you guarantee a refund?", a: "No — and you should be cautious of anyone who does. Every tax situation is different. What we provide is careful, accurate preparation and organization so your filing reflects your real situation." },
        { q: "Can you help if I've never filed in the U.S.?", a: "Yes. We regularly help individuals and families who are navigating U.S. tax obligations for the first time, including ITIN applications where applicable." },
        { q: "Do you work with small businesses?", a: "Yes. We help small businesses organize their accounting, understand their tax obligations and keep personal and business finances properly separated." },
      ],
      disclaimer: "Tax outcomes depend on individual circumstances. No refund amount or tax result is guaranteed.",
    },
    es: {
      title: "Consultoría Financiera",
      short: "Orientación fiscal, apoyo contable y organización financiera completa — para individuos y empresas.",
      heroSub: "Registros organizados. Declaraciones informadas. Sin sorpresas.",
      seoTitle: "Consultoría Financiera y Fiscal en Lake Nona, Orlando | Aviah Financial",
      seoDesc: "Consultoría financiera, orientación fiscal y apoyo contable para individuos y empresas en Lake Nona, Orlando — incluyendo apoyo con ITIN. Bilingüe, con cita previa.",
      overview: [
        "Los impuestos tocan casi todas las demás decisiones financieras que tomas — la estructura de tu negocio, tus inversiones, tu plan patrimonial. Abordamos tu vida financiera como un sistema conectado, con los impuestos y la contabilidad como su columna vertebral — no como una transacción de una vez al año.",
        "Aviah Financial apoya a individuos y empresas con orientación fiscal, apoyo contable, organización financiera y conversaciones de planificación — para que siempre sepas dónde estás parado y qué sigue.",
      ],
      whoFor: [
        "Individuos con obligaciones fiscales personales",
        "Profesionales independientes y contratistas",
        "Dueños de pequeños negocios que necesitan libros organizados",
        "Nuevos residentes de EE. UU. que navegan el sistema fiscal por primera vez",
      ],
      challenges: [
        "Registros desorganizados que hacen estresante cada temporada de impuestos",
        "Incertidumbre sobre qué obligaciones aplican a tu situación",
        "Finanzas personales y del negocio mezcladas",
        "Documentos en dos idiomas y requisitos en uno",
      ],
      evaluates: [
        "Tu estado de declaración actual y tus obligaciones",
        "Cómo la estructura de tu negocio afecta tus impuestos",
        "El estado de tus libros y registros",
        "Si un ITIN aplica para alguien en tu situación",
        "Cómo las decisiones fiscales se conectan con tu estrategia general",
      ],
      support: [
        "Apoyo en la preparación de impuestos individuales",
        "Apoyo en la preparación de impuestos empresariales",
        "Organización contable y de libros",
        "Asistencia con solicitudes de ITIN como Agente Tramitador Certificado",
        "Sistemas de organización financiera para claridad continua",
      ],
      faqs: [
        { q: "¿Garantizan un reembolso?", a: "No — y conviene desconfiar de quien lo haga. Cada situación fiscal es diferente. Lo que ofrecemos es una preparación cuidadosa y precisa para que tu declaración refleje tu situación real." },
        { q: "¿Pueden ayudarme si nunca he declarado en EE. UU.?", a: "Sí. Con frecuencia ayudamos a individuos y familias que navegan las obligaciones fiscales de EE. UU. por primera vez, incluyendo solicitudes de ITIN cuando corresponde." },
        { q: "¿Trabajan con pequeños negocios?", a: "Sí. Ayudamos a pequeños negocios a organizar su contabilidad, comprender sus obligaciones fiscales y mantener las finanzas personales y empresariales correctamente separadas." },
      ],
      disclaimer: "Los resultados fiscales dependen de las circunstancias individuales. No se garantiza ningún monto de reembolso ni resultado fiscal.",
    },
  },
  {
    slug: "asset-protection",
    num: "03",
    status: "active",
    img: "marble",
    en: {
      title: "Asset Protection",
      short: "Strategic guidance to help you evaluate ways to organize and protect what you have built.",
      heroSub: "What took years to build deserves intentional protection.",
      seoTitle: "Asset Protection Guidance in Orlando | Aviah Financial",
      seoDesc: "Strategic asset protection guidance in Lake Nona, Orlando — organizing and protecting assets according to your objectives. Bilingual consultations.",
      overview: [
        "Asset protection is not about hiding — it is about organizing. It means looking at what you own, how it is structured, and whether that structure actually serves the future you intend.",
        "Aviah Financial provides strategic guidance intended to help clients evaluate ways to organize and protect their assets and resources according to their objectives and circumstances — always as part of the complete financial picture.",
      ],
      whoFor: [
        "Business owners whose personal and business assets overlap",
        "Families with property across more than one country",
        "Professionals exposed to liability through their work",
        "Anyone who has built something worth protecting deliberately",
      ],
      challenges: [
        "Assets titled in ways that create unnecessary exposure",
        "No clear picture of everything you own and how it connects",
        "Growth that has outpaced the structures around it",
        "Confusion about what protection actually means — and what it doesn't",
      ],
      evaluates: [
        "What you own and how each asset is structured",
        "Where personal and business exposure overlap",
        "How your assets connect to your estate and legacy plans",
        "Gaps between your current structure and your objectives",
      ],
      support: [
        "Asset inventory and organization guidance",
        "Structural evaluation aligned with your objectives",
        "Coordination with estate planning and business structure",
        "Referral to qualified legal professionals where legal instruments are required",
      ],
      faqs: [
        { q: "Can you guarantee my assets are fully protected?", a: "No honest professional can promise complete legal protection. What we do is help you evaluate your structure, understand your exposure and organize your assets intentionally — with qualified attorneys involved wherever legal instruments are needed." },
        { q: "Is asset protection only for wealthy people?", a: "No. Anyone who owns a home, runs a business or has savings has something worth organizing. The strategy scales with the situation." },
        { q: "How does this connect to estate planning?", a: "Directly. The way assets are organized determines how they are protected in life and how they transfer later — which is why we evaluate both together." },
      ],
      disclaimer: "Asset protection guidance is strategic and organizational. It does not constitute legal advice and cannot promise complete legal protection. Where legal instruments are required, qualified attorneys are involved.",
    },
    es: {
      title: "Protección de Activos",
      short: "Orientación estratégica para ayudarte a evaluar formas de organizar y proteger lo que has construido.",
      heroSub: "Lo que tomó años construir merece una protección intencional.",
      seoTitle: "Orientación en Protección de Activos en Orlando | Aviah Financial",
      seoDesc: "Orientación estratégica en protección de activos en Lake Nona, Orlando — organizando y protegiendo activos según tus objetivos. Consultas bilingües.",
      overview: [
        "La protección de activos no se trata de esconder — se trata de organizar. Significa mirar lo que posees, cómo está estructurado, y si esa estructura realmente sirve al futuro que deseas.",
        "Aviah Financial ofrece orientación estratégica destinada a ayudar a los clientes a evaluar formas de organizar y proteger sus activos y recursos de acuerdo con sus objetivos y circunstancias — siempre como parte del panorama financiero completo.",
      ],
      whoFor: [
        "Dueños de negocio cuyos activos personales y empresariales se superponen",
        "Familias con propiedades en más de un país",
        "Profesionales expuestos a responsabilidad por su trabajo",
        "Cualquier persona que haya construido algo que vale la pena proteger deliberadamente",
      ],
      challenges: [
        "Activos titulados de formas que crean exposición innecesaria",
        "Sin una imagen clara de todo lo que posees y cómo se conecta",
        "Un crecimiento que ha superado las estructuras que lo rodean",
        "Confusión sobre lo que la protección realmente significa — y lo que no",
      ],
      evaluates: [
        "Qué posees y cómo está estructurado cada activo",
        "Dónde se superpone la exposición personal y empresarial",
        "Cómo tus activos se conectan con tus planes patrimoniales y de legado",
        "Brechas entre tu estructura actual y tus objetivos",
      ],
      support: [
        "Orientación en inventario y organización de activos",
        "Evaluación estructural alineada con tus objetivos",
        "Coordinación con planificación patrimonial y estructura empresarial",
        "Referencia a profesionales legales calificados cuando se requieren instrumentos legales",
      ],
      faqs: [
        { q: "¿Pueden garantizar que mis activos estén totalmente protegidos?", a: "Ningún profesional honesto puede prometer protección legal completa. Lo que hacemos es ayudarte a evaluar tu estructura, comprender tu exposición y organizar tus activos de forma intencional — con abogados calificados involucrados donde se requieran instrumentos legales." },
        { q: "¿La protección de activos es solo para personas adineradas?", a: "No. Cualquier persona que posea una casa, tenga un negocio o tenga ahorros tiene algo que vale la pena organizar. La estrategia escala con la situación." },
        { q: "¿Cómo se conecta esto con la planificación patrimonial?", a: "Directamente. La forma en que se organizan los activos determina cómo se protegen en vida y cómo se transfieren después — por eso evaluamos ambos juntos." },
      ],
      disclaimer: "La orientación en protección de activos es estratégica y organizacional. No constituye asesoría legal y no puede prometer protección legal completa. Cuando se requieren instrumentos legales, se involucran abogados calificados.",
    },
  },
  {
    slug: "estate-planning",
    num: "04",
    status: "active",
    img: "staircase",
    en: {
      title: "Estate Planning",
      short: "Planning to organize, protect and transfer what you have built — in alignment with your family's goals.",
      heroSub: "Legacy is not what you leave. It is what you set in order.",
      seoTitle: "Estate Planning Guidance in Lake Nona, Orlando | Aviah Financial",
      seoDesc: "Estate planning guidance in Lake Nona, Orlando — organizing, protecting and transferring assets in alignment with family goals. Bilingual.",
      overview: [
        "Estate planning is not only about what happens later — it is about clarity now. It means your assets are organized, your wishes are documented and your family is not left to guess.",
        "Aviah Financial focuses on planning to organize, protect and transfer assets and wealth in alignment with the goals of the client and family. Where legal documents or legal services require an attorney, we work alongside qualified, independent legal professionals — Aviah Financial is not a law firm.",
      ],
      whoFor: [
        "Parents who want their children's future clearly provided for",
        "Families with assets in more than one country",
        "Business owners whose estate includes a company",
        "Anyone whose family would struggle to untangle their affairs today",
      ],
      challenges: [
        "Postponement — estate planning feels like something for later",
        "Family members in different countries, languages and systems",
        "A business with no documented continuity plan",
        "Assets acquired over years without a unifying structure",
      ],
      evaluates: [
        "What your estate actually consists of — assets, accounts, property, business interests",
        "Who depends on you and what they would need",
        "How your assets are titled and where documents are missing",
        "How your estate plan connects to taxes and asset protection",
      ],
      support: [
        "Estate organization and planning guidance",
        "Family goal alignment and documentation planning",
        "Coordination with qualified attorneys for legal documents",
        "Connection of estate decisions to tax and protection strategy",
      ],
      faqs: [
        { q: "Do you draft wills or trusts?", a: "Legal documents that require an attorney are prepared by qualified, independent legal professionals. Our role is planning: helping you organize, decide and coordinate — then working alongside the attorneys who execute the legal instruments." },
        { q: "When should a family start estate planning?", a: "Earlier than most people think. If you own assets, have children or run a business, there is already something to organize. Planning is easier — and kinder — done calmly rather than in a crisis." },
        { q: "Is Aviah Financial a law firm?", a: "No. Aviah Financial is a financial and business consulting practice and does not provide legal services. We coordinate with independent attorneys where the law requires them." },
      ],
      disclaimer: "Aviah Financial is not a law firm and does not provide legal services. Estate planning that requires legal documents or legal advice is carried out with qualified, independent attorneys.",
    },
    es: {
      title: "Planificación Patrimonial",
      short: "Planificación para organizar, proteger y transferir lo que has construido — alineado con las metas de tu familia.",
      heroSub: "El legado no es lo que dejas. Es lo que pones en orden.",
      seoTitle: "Orientación en Planificación Patrimonial en Lake Nona, Orlando | Aviah Financial",
      seoDesc: "Orientación en planificación patrimonial en Lake Nona, Orlando — organizando, protegiendo y transfiriendo activos alineados con las metas familiares. Bilingüe.",
      overview: [
        "La planificación patrimonial no se trata solo de lo que pasa después — se trata de claridad ahora. Significa que tus activos están organizados, tus deseos documentados y tu familia no se queda adivinando.",
        "Aviah Financial se enfoca en la planificación para organizar, proteger y transferir activos y patrimonio en alineación con las metas del cliente y su familia. Cuando los documentos o servicios legales requieren un abogado, trabajamos junto a profesionales legales calificados e independientes — Aviah Financial no es un bufete de abogados.",
      ],
      whoFor: [
        "Padres que quieren el futuro de sus hijos claramente asegurado",
        "Familias con activos en más de un país",
        "Dueños de negocio cuyo patrimonio incluye una empresa",
        "Cualquier persona cuya familia tendría dificultades para ordenar sus asuntos hoy",
      ],
      challenges: [
        "La postergación — la planificación patrimonial parece cosa de más adelante",
        "Familiares en diferentes países, idiomas y sistemas",
        "Un negocio sin un plan de continuidad documentado",
        "Activos adquiridos con los años sin una estructura unificadora",
      ],
      evaluates: [
        "De qué consiste realmente tu patrimonio — activos, cuentas, propiedades, intereses empresariales",
        "Quién depende de ti y qué necesitaría",
        "Cómo están titulados tus activos y qué documentos faltan",
        "Cómo tu plan patrimonial se conecta con impuestos y protección de activos",
      ],
      support: [
        "Orientación en organización y planificación patrimonial",
        "Alineación de metas familiares y planificación de documentación",
        "Coordinación con abogados calificados para documentos legales",
        "Conexión de decisiones patrimoniales con estrategia fiscal y de protección",
      ],
      faqs: [
        { q: "¿Redactan testamentos o fideicomisos?", a: "Los documentos legales que requieren un abogado son preparados por profesionales legales calificados e independientes. Nuestro rol es la planificación: ayudarte a organizar, decidir y coordinar — y luego trabajar junto a los abogados que ejecutan los instrumentos legales." },
        { q: "¿Cuándo debería una familia comenzar la planificación patrimonial?", a: "Antes de lo que la mayoría cree. Si posees activos, tienes hijos o manejas un negocio, ya hay algo que organizar. Planificar es más fácil — y más amable — hecho con calma y no en una crisis." },
        { q: "¿Aviah Financial es un bufete de abogados?", a: "No. Aviah Financial es una práctica de consultoría financiera y empresarial y no proporciona servicios legales. Coordinamos con abogados independientes donde la ley lo requiere." },
      ],
      disclaimer: "Aviah Financial no es un bufete de abogados y no proporciona servicios legales. La planificación patrimonial que requiere documentos legales o asesoría legal se realiza con abogados independientes calificados.",
    },
  },
  {
    slug: "strategic-investments",
    num: "05",
    status: "active",
    img: "hero",
    en: {
      title: "Strategic Investments",
      short: "Guidance to evaluate potential investment opportunities in relation to your objectives and long-term strategy.",
      heroSub: "Investing begins with knowing what the money is for.",
      seoTitle: "Strategic Investment Guidance in Orlando | Aviah Financial",
      seoDesc: "Guidance to evaluate investment opportunities aligned with your objectives and long-term strategy. Lake Nona, Orlando. Bilingual. No guaranteed returns.",
      overview: [
        "An investment decision made in isolation is a guess. The same opportunity can be right for one person and wrong for another — depending on their taxes, their business, their family and their timeline.",
        "Aviah Financial provides guidance to help clients evaluate potential investment opportunities in relation to their objectives, needs, circumstances and long-term strategy. We make no promises of profit and no guarantees of return — we provide the clarity that should come before any decision.",
      ],
      whoFor: [
        "First-generation investors building a foundation",
        "Business owners with profits to deploy deliberately",
        "Families planning for long-term, multi-generational goals",
        "Anyone evaluating an opportunity who wants a second, disinterested look",
      ],
      challenges: [
        "Opportunities evaluated by enthusiasm instead of fit",
        "No framework connecting investments to taxes and estate plans",
        "Information in a language that isn't your strongest",
        "Pressure tactics disguised as advice",
      ],
      evaluates: [
        "Your objectives, timeline and risk tolerance",
        "How an opportunity interacts with your tax situation",
        "How it fits your business and family circumstances",
        "What you genuinely understand — and what needs explaining first",
      ],
      support: [
        "Opportunity evaluation against your objectives",
        "Financial education before any decision",
        "Long-term strategy alignment",
        "Coordination with licensed specialists where products require them",
      ],
      faqs: [
        { q: "Do you guarantee investment returns?", a: "Never. All investing involves risk, including possible loss of principal. Anyone who guarantees returns is telling you something untrue. Our role is evaluation, education and strategy — not promises." },
        { q: "Are you a registered investment adviser?", a: "Aviah Financial provides educational and strategic guidance. Where specific securities products or registered advice are required, we coordinate with appropriately licensed professionals." },
        { q: "I was offered an opportunity. Can you look at it?", a: "Yes — a calm, disinterested evaluation is often the most valuable step. We help you understand what you're actually being offered and how it would fit your complete picture." },
      ],
      disclaimer: "Nothing on this page is an offer, solicitation or recommendation of any security or investment product. All investing involves risk, including possible loss of principal. No return is guaranteed.",
    },
    es: {
      title: "Inversiones Estratégicas",
      short: "Orientación para evaluar oportunidades de inversión en relación con tus objetivos y estrategia a largo plazo.",
      heroSub: "Invertir comienza con saber para qué es el dinero.",
      seoTitle: "Orientación en Inversiones Estratégicas en Orlando | Aviah Financial",
      seoDesc: "Orientación para evaluar oportunidades de inversión alineadas con tus objetivos y estrategia a largo plazo. Lake Nona, Orlando. Bilingüe. Sin rendimientos garantizados.",
      overview: [
        "Una decisión de inversión tomada de forma aislada es una apuesta. La misma oportunidad puede ser correcta para una persona y equivocada para otra — dependiendo de sus impuestos, su negocio, su familia y su horizonte de tiempo.",
        "Aviah Financial ofrece orientación para ayudar a los clientes a evaluar posibles oportunidades de inversión en relación con sus objetivos, necesidades, circunstancias y estrategia a largo plazo. No prometemos ganancias ni garantizamos rendimientos — proporcionamos la claridad que debe existir antes de cualquier decisión.",
      ],
      whoFor: [
        "Inversionistas de primera generación construyendo una base",
        "Dueños de negocio con ganancias para desplegar deliberadamente",
        "Familias planificando metas a largo plazo y multigeneracionales",
        "Cualquiera que evalúe una oportunidad y quiera una segunda mirada desinteresada",
      ],
      challenges: [
        "Oportunidades evaluadas por entusiasmo en lugar de conveniencia",
        "Sin un marco que conecte las inversiones con impuestos y planes patrimoniales",
        "Información en un idioma que no es tu más fuerte",
        "Tácticas de presión disfrazadas de asesoría",
      ],
      evaluates: [
        "Tus objetivos, horizonte de tiempo y tolerancia al riesgo",
        "Cómo una oportunidad interactúa con tu situación fiscal",
        "Cómo encaja con tus circunstancias empresariales y familiares",
        "Qué comprendes genuinamente — y qué necesita explicación primero",
      ],
      support: [
        "Evaluación de oportunidades contra tus objetivos",
        "Educación financiera antes de cualquier decisión",
        "Alineación con la estrategia a largo plazo",
        "Coordinación con especialistas licenciados donde los productos lo requieren",
      ],
      faqs: [
        { q: "¿Garantizan rendimientos de inversión?", a: "Nunca. Toda inversión implica riesgo, incluida la posible pérdida del capital. Quien garantiza rendimientos te está diciendo algo falso. Nuestro rol es la evaluación, la educación y la estrategia — no las promesas." },
        { q: "¿Son asesores de inversión registrados?", a: "Aviah Financial proporciona orientación educativa y estratégica. Cuando se requieren productos de valores específicos o asesoría registrada, coordinamos con profesionales debidamente licenciados." },
        { q: "Me ofrecieron una oportunidad. ¿Pueden revisarla?", a: "Sí — una evaluación tranquila y desinteresada suele ser el paso más valioso. Te ayudamos a entender qué te están ofreciendo realmente y cómo encajaría en tu panorama completo." },
      ],
      disclaimer: "Nada en esta página constituye una oferta, solicitud o recomendación de ningún valor o producto de inversión. Toda inversión implica riesgo, incluida la posible pérdida del capital. No se garantiza ningún rendimiento.",
    },
  },
  {
    slug: "immigration",
    num: "—",
    status: "inactive",
    img: "meeting",
    en: {
      title: "Immigration Support",
      short: "Professional support and guidance with immigration processes and administrative needs.",
      heroSub: "Processes are easier when someone explains the map.",
      seoTitle: "Immigration Support Services in Orlando | Aviah Financial",
      seoDesc: "Administrative immigration support and process guidance in Lake Nona, Orlando. Bilingual. Administrative assistance — not legal representation.",
      overview: [
        "Immigration processes touch everything else: work, taxes, business, family. When they are handled in isolation, mistakes echo for years.",
        "Aviah Financial provides professional support and guidance related to immigration processes and administrative needs. This is administrative and process assistance — not legal representation. Aviah Financial is not a law firm and does not provide legal advice on immigration matters.",
      ],
      whoFor: [
        "Individuals navigating immigration paperwork for the first time",
        "Families coordinating processes across borders",
        "Entrepreneurs whose business plans depend on their status",
        "Anyone who needs forms, translations and steps organized clearly",
      ],
      challenges: [
        "Complex forms and instructions in a second language",
        "Not knowing which process applies to your situation",
        "Missed steps that delay everything else",
        "Confusion between administrative help and legal representation",
      ],
      evaluates: [
        "Which process your situation actually calls for",
        "What documentation each step requires",
        "How immigration timelines interact with work, taxes and business plans",
        "Where a qualified immigration attorney is needed instead",
      ],
      support: [
        "Administrative and process guidance",
        "Document organization and form preparation support",
        "ITIN application assistance as a Certified Acceptance Agent",
        "Referral to qualified immigration attorneys where legal representation is required",
      ],
      faqs: [
        { q: "Are you immigration attorneys?", a: "No. Aviah Financial is not a law firm and does not provide legal representation or legal advice on immigration matters. We provide administrative and process support — and when your situation requires an attorney, we tell you clearly and refer you to one." },
        { q: "What is the difference between administrative help and legal representation?", a: "Administrative help means organizing documents, preparing forms and guiding you through process steps. Legal representation means an attorney advises you on the law and represents you before authorities. We do the first; licensed attorneys do the second." },
        { q: "Can you help with ITIN applications?", a: "Yes. As a Certified Acceptance Agent, we can assist with ITIN applications, including verifying documentation — which often saves applicants from mailing original documents." },
      ],
      disclaimer: "Immigration support at Aviah Financial is administrative and process assistance. It is not legal representation and does not constitute legal advice. Aviah Financial is not a law firm.",
    },
    es: {
      title: "Apoyo Migratorio",
      short: "Apoyo profesional y orientación en procesos migratorios y necesidades administrativas.",
      heroSub: "Los procesos son más fáciles cuando alguien te explica el mapa.",
      seoTitle: "Servicios de Apoyo Migratorio en Orlando | Aviah Financial",
      seoDesc: "Apoyo migratorio administrativo y orientación de procesos en Lake Nona, Orlando. Bilingüe. Asistencia administrativa — no representación legal.",
      overview: [
        "Los procesos migratorios tocan todo lo demás: trabajo, impuestos, negocios, familia. Cuando se manejan de forma aislada, los errores resuenan por años.",
        "Aviah Financial ofrece apoyo profesional y orientación relacionada con procesos migratorios y necesidades administrativas. Esto es asistencia administrativa y de procesos — no representación legal. Aviah Financial no es un bufete de abogados y no proporciona asesoría legal en asuntos migratorios.",
      ],
      whoFor: [
        "Personas que navegan trámites migratorios por primera vez",
        "Familias coordinando procesos a través de fronteras",
        "Emprendedores cuyos planes de negocio dependen de su estatus",
        "Cualquiera que necesite formularios, traducciones y pasos organizados con claridad",
      ],
      challenges: [
        "Formularios e instrucciones complejos en un segundo idioma",
        "No saber qué proceso aplica a tu situación",
        "Pasos perdidos que retrasan todo lo demás",
        "Confusión entre ayuda administrativa y representación legal",
      ],
      evaluates: [
        "Qué proceso realmente requiere tu situación",
        "Qué documentación exige cada paso",
        "Cómo los tiempos migratorios interactúan con trabajo, impuestos y planes de negocio",
        "Dónde se necesita un abogado de inmigración calificado",
      ],
      support: [
        "Orientación administrativa y de procesos",
        "Organización de documentos y apoyo en la preparación de formularios",
        "Asistencia con solicitudes de ITIN como Agente Tramitador Certificado",
        "Referencia a abogados de inmigración calificados donde se requiere representación legal",
      ],
      faqs: [
        { q: "¿Son abogados de inmigración?", a: "No. Aviah Financial no es un bufete de abogados y no proporciona representación legal ni asesoría legal en asuntos migratorios. Ofrecemos apoyo administrativo y de procesos — y cuando tu situación requiere un abogado, te lo decimos con claridad y te referimos a uno." },
        { q: "¿Cuál es la diferencia entre ayuda administrativa y representación legal?", a: "La ayuda administrativa significa organizar documentos, preparar formularios y guiarte por los pasos del proceso. La representación legal significa que un abogado te asesora sobre la ley y te representa ante las autoridades. Nosotros hacemos lo primero; los abogados licenciados hacen lo segundo." },
        { q: "¿Pueden ayudar con solicitudes de ITIN?", a: "Sí. Como Agente Tramitador Certificado, podemos asistir con solicitudes de ITIN, incluyendo la verificación de documentos — lo que a menudo evita que tengas que enviar documentos originales por correo." },
      ],
      disclaimer: "El apoyo migratorio en Aviah Financial es asistencia administrativa y de procesos. No es representación legal y no constituye asesoría legal. Aviah Financial no es un bufete de abogados.",
    },
  },
];
